/**
 * KIAN Assistant Chat Component
 *
 * Main chat interface component that combines message list and input form.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Group, Button, ActionIcon, Menu, Title, Text, Loader, Center, Modal, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconArchive, IconTrash, IconDots, IconWifiOff } from '@tabler/icons-react';
import { useAssistantChat } from '../../hooks/useAssistantChat';
import { isReplyBusy } from '../../hooks/assistantPendingReplies';
import type { AssistantPageContextPayload } from '../../lib/api/domains/assistant';
import { ChatMessageList } from './ChatMessageList';
import { ChatInputForm } from './ChatInputForm';
import { KnowledgeBaseSuggestions } from './KnowledgeBaseSuggestions';
import { entityKey } from '../../types/kian';
import { SCRATCH_KEY, useKianFlyoutStore } from '../../stores/kianFlyout';
import { useNavigatorOnline } from '../../hooks/useNavigatorOnline';
import { useKianAnswerNotification } from '../../hooks/useKianAnswerNotification';
import { useVoiceConversation, type VoiceReply } from './voice/useVoiceConversation';
import { VoiceConversationPanel } from './voice/VoiceConversationPanel';
import { useAuthStore } from '../../stores/auth';

interface AssistantChatProps {
  sessionId?: string;
  submissionId?: string;
  onBack?: () => void;
  initialTitle?: string;
  /** KIAN v2 Flyout: Seitenkontext für bessere Antworten */
  pageContext?: AssistantPageContextPayload;
  /** Streaming-Antworten (nur ohne Datei-Upload) */
  streamReplies?: boolean;
  /** Flyout: kein voller Header / keine KB-Seitenleiste */
  embedMode?: boolean;
  /** Archivierter Chat: Read-only-Modus mit Wiederherstellen-Banner */
  readOnly?: boolean;
  /** Handler für "Wiederherstellen" im read-only Modus */
  onRestore?: () => Promise<void> | void;
  /** KIAN-Fenster: äußere Kopfzeile übernimmt Titel/Aktionen */
  hideTopBar?: boolean;
  /** Wissens-Vorschläge rechts (nur Vollbild) — das KIAN-Fenster hat dort seine Kontextspalte */
  showKnowledgeSuggestions?: boolean;
}

export function AssistantChat({
  sessionId,
  submissionId,
  onBack,
  initialTitle,
  pageContext,
  streamReplies = false,
  embedMode = false,
  readOnly = false,
  onRestore,
  hideTopBar = false,
  showKnowledgeSuggestions = true,
}: AssistantChatProps) {
  const [currentUserMessage, setCurrentUserMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<{ label: string; onConfirm: () => Promise<void> | void } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const isOnline = useNavigatorOnline();
  const flyoutPanelMode = useKianFlyoutStore((s) => s.panelMode);

  const {
    currentSession,
    isLoading,
    pendingReplies,
    error,
    createSession,
    loadSession,
    sendMessage,
    abortStream,
    regenerateMessage,
    archiveSession,
    deleteSession,
    appendMessages,
    setSessionToolBudget,
  } = useAssistantChat();
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');

  // Nur die Antwort DIESES Gesprächs — läuft eine in einem anderen, ist hier nichts gesperrt
  const pendingReply = currentSession ? pendingReplies[currentSession.id] : undefined;
  const isSending = isReplyBusy(pendingReply);
  const isStreaming = isSending && pendingReply?.kind === 'stream';
  const isAnalyzingFiles = isSending && pendingReply?.isAnalyzingFiles === true;
  const uploadProgress = pendingReply?.uploadProgress ?? null;

  // Im Vollbild (KIAN-Fenster) ist die Antwort ohnehin zu sehen — Hinweis nur für den Flyout.
  useKianAnswerNotification(isSending || isStreaming, embedMode ? flyoutPanelMode !== 'closed' : true);

  // Gesprächsmodus: wartet auf KIANs nächste fertige Antwort und liest sie vor
  const messages = currentSession?.messages;
  const lastReply = useMemo<VoiceReply | null>(() => {
    const last = [...(messages ?? [])].reverse().find((m) => m.role === 'ASSISTANT');
    if (!last) return null;
    const draft = last.metadata?.voiceDraft as VoiceReply['voiceDraft'] | undefined;
    return {
      id: last.id,
      content: last.content,
      streaming: last.metadata?.streaming === true,
      voiceDraft: draft && typeof draft.actionId === 'string' ? draft : null,
    };
  }, [messages]);
  const sendByVoice = useCallback(
    (text: string) =>
      sendMessage(text, undefined, pageContext, {
        useStream: streamReplies,
        // Sprachmodus: KIAN antwortet kurz und vorlesbar (services/kian/voice/voicePrompt.ts)
        hints: { voice: true },
      }),
    [sendMessage, pageContext, streamReplies],
  );
  const sessionIdForVoice = currentSession?.id ?? null;
  const voice = useVoiceConversation({
    sessionId: sessionIdForVoice,
    send: sendByVoice,
    // „Ja, anlegen", Ergebnis & Co. speichert der Server — hier nur noch anzeigen
    onMessages: (msgs) => {
      if (sessionIdForVoice) appendMessages(sessionIdForVoice, msgs);
    },
    lastReply,
    busy: isSending || isStreaming,
  });

  // Session-Daten laden: Store hat oft schon currentSessionId (neues Gespräch / Auswahl Liste),
  // aber currentSession erst nach GET /assistant/chat/:id — daher immer loadSession bei bekannter id.
  useEffect(() => {
    const initSession = async () => {
      try {
        if (sessionId) {
          await loadSession(sessionId);
          return;
        }
        const newSessionId = await createSession({ submissionId, title: initialTitle });
        await loadSession(newSessionId);
      } catch (error) {
        console.error('Failed to initialize session:', error);
      }
    };

    void initSession();
  }, [sessionId, submissionId, initialTitle, loadSession, createSession]);

  const flyoutDraftKey = embedMode
    ? pageContext?.primary
      ? entityKey(pageContext.primary)
      : SCRATCH_KEY
    : undefined;
  const flyoutSlashType = embedMode ? pageContext?.primary?.type ?? null : null;

  if (isLoading && !currentSession) {
    return (
      <Center style={{ height: '400px' }}>
        <Stack align="center" gap="sm">
          <Loader />
          <Text c="dimmed">Session wird geladen...</Text>
        </Stack>
      </Center>
    );
  }

  if (!currentSession) {
    return (
      <Box p="md">
        <Text c="red">Fehler: Session konnte nicht geladen werden.</Text>
        {error ? (
          <Text size="sm" c="dimmed" mt="xs">
            {error}
          </Text>
        ) : null}
      </Box>
    );
  }

  const handleSendMessage = async (
    message: string,
    files?: File[],
    hints?: Record<string, unknown>
  ) => {
    setCurrentUserMessage('');
    await sendMessage(message, files, pageContext, {
      useStream: streamReplies && (!files || files.length === 0),
      hints,
    });
  };

  const skipOuterHeader = embedMode || hideTopBar;

  return (
    <>
    <Box
      style={{
        height: '100%',
        minHeight: 0,
        display: 'grid',
        gridTemplateRows: skipOuterHeader
          ? 'minmax(0, 1fr) auto'
          : 'auto minmax(0, 1fr) auto',
        gridTemplateColumns: '1fr',
      }}
    >
      {!embedMode && !hideTopBar && (
        <Group p="md" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }} justify="space-between">
          <Group gap="sm">
            {onBack && (
              <ActionIcon variant="default" onClick={onBack}>
                <IconArrowLeft size={18} />
              </ActionIcon>
            )}
            <div>
              <Title order={4}>{currentSession.title}</Title>
              <Text size="xs" c="dimmed">
                {currentSession.messages.length} Nachrichten
              </Text>
            </div>
          </Group>

          <Menu position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconArchive size={14} />}
                onClick={() => {
                  setConfirmAction({
                    label: 'Dieses Gespräch archivieren?',
                    onConfirm: async () => { await archiveSession(currentSession.id); onBack?.(); },
                  });
                }}
              >
                Archivieren
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} />}
                onClick={() => {
                  setConfirmAction({
                    label: 'Dieses Gespräch unwiderruflich löschen?',
                    onConfirm: async () => { await deleteSession(currentSession.id); onBack?.(); },
                  });
                }}
              >
                Löschen
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      )}

      {!isOnline && (
        <Alert
          icon={<IconWifiOff size={16} />}
          color="yellow"
          variant="light"
          py={6}
          px="md"
          radius={0}
          style={{ flexShrink: 0 }}
        >
          Kein Internetzugang — Nachrichten können erst gesendet werden, wenn du wieder online bist.
        </Alert>
      )}

      <Box
        style={{
          minHeight: 0,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateRows: '1fr',
          gridTemplateColumns: embedMode || !showKnowledgeSuggestions ? '1fr' : '1fr auto',
        }}
      >
        <ChatMessageList
          messages={currentSession.messages}
          isLoading={isSending}
          sessionId={currentSession.id}
          entityType={pageContext?.primary?.type ?? null}
          isFullscreen={!embedMode}
          readOnly={readOnly}
          onRegenerateMessage={
            readOnly ? undefined : (messageId) => regenerateMessage(messageId, pageContext, streamReplies)
          }
          onRegenerateDeepMessage={
            readOnly
              ? undefined
              : (messageId) => regenerateMessage(messageId, pageContext, streamReplies, { forceDeep: true })
          }
          toolBudget={currentSession.toolBudget}
          canRaiseToolBudget={isAdmin}
          onRaiseToolBudget={
            readOnly
              ? undefined
              : async (messageId, perMessage) => {
                  if (perMessage != null) await setSessionToolBudget(currentSession.id, perMessage);
                  await regenerateMessage(messageId, pageContext, streamReplies);
                }
          }
        />

        {!embedMode && showKnowledgeSuggestions && (
          <Box
            visibleFrom="xl"
            style={{
              minWidth: '280px',
              maxWidth: '280px',
              overflowY: 'auto',
              padding: '1rem',
              borderLeft: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <KnowledgeBaseSuggestions userMessage={currentUserMessage} isLoading={isSending} />
          </Box>
        )}
      </Box>

      {/* Eigener Layer: immer über der Message-List (z. B. lange Bubbles, Scroll-Überlapp), nichts „darüber“ */}
      <Box
        style={{
          position: 'relative',
          zIndex: 30,
          isolation: 'isolate',
          flexShrink: 0,
          background: 'var(--mantine-color-body)',
          boxShadow: '0 -4px 14px rgba(0, 0, 0, 0.18)',
        }}
      >
        <VoiceConversationPanel voice={voice} />
        <ChatInputForm
          onSendMessage={handleSendMessage}
          onStartVoiceConversation={readOnly ? undefined : () => void voice.start()}
          voiceConversationActive={voice.active}
          onInputChange={setCurrentUserMessage}
          disabled={isLoading || readOnly || !isOnline}
          isLoading={isSending}
          error={error}
          draftContextKey={flyoutDraftKey}
          entityTypeForSlash={flyoutSlashType}
          onAbortStream={isStreaming ? () => abortStream(currentSession.id) : undefined}
          isAnalyzingFiles={isAnalyzingFiles}
          uploadProgress={uploadProgress}
          readOnly={readOnly}
          onRestore={onRestore}
          reserveFabAffixSpace={embedMode}
        />
      </Box>
    </Box>

      <Modal
        opened={confirmAction !== null}
        onClose={() => { if (!confirmLoading) setConfirmAction(null); }}
        title="Bestätigung"
        centered
        size="sm"
        closeOnClickOutside={!confirmLoading}
        closeOnEscape={!confirmLoading}
      >
        <Stack gap="md">
          <Text size="sm">{confirmAction?.label}</Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" disabled={confirmLoading} onClick={() => setConfirmAction(null)}>
              Abbrechen
            </Button>
            <Button
              color="red"
              loading={confirmLoading}
              onClick={async () => {
                setConfirmLoading(true);
                try {
                  await confirmAction?.onConfirm();
                } catch {
                  notifications.show({
                    color: 'red',
                    title: 'Fehler',
                    message: 'Aktion konnte nicht ausgeführt werden.',
                  });
                } finally {
                  setConfirmLoading(false);
                  setConfirmAction(null);
                }
              }}
            >
              Bestätigen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
